import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Compact variant for use inside cards. */
  dense?: boolean;
}

/** Friendly "nothing here yet" / "not available" block with an optional call to action. */
export function EmptyState({ icon, title, description, action, dense }: EmptyStateProps) {
  return (
    <Stack
      spacing={1.5}
      sx={{ alignItems: 'center', textAlign: 'center', py: dense ? 4 : 8, px: 2, mx: 'auto' }}
    >
      {icon && (
        <Box
          aria-hidden
          sx={(theme) => ({
            width: dense ? 48 : 64,
            height: dense ? 48 : 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: theme.vars.palette.primary.container,
            color: theme.vars.palette.primary.onContainer,
            '& svg': { fontSize: dense ? 24 : 32 },
          })}
        >
          {icon}
        </Box>
      )}
      <Typography variant={dense ? 'subtitle1' : 'h6'} component="h2">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}
